import React from 'react';
import {
    Stack,
    Group,
    Button,
    Select,
    TextInput,
    NumberInput,
    Text,
    ThemeIcon,
    Box,
    ActionIcon,
    Divider,
    Center,
    Switch,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import plusIcon from '../../assets/icons/plus.svg';
import trashIcon from '../../assets/icons/trash.svg';
import { RuleCondition, createEmptyCondition } from '../../hooks/useRules';
import type { RuleField, RuleOperator } from '../../hooks/useRules';
import type { Transaction } from '../../types/transaction';
import type { BudgetCategory, BudgetHeader } from '../../hooks/useBudgetCategories';

interface RuleConditionsControlsProps {
    conditions: RuleCondition[];
    onChange: (conditions: RuleCondition[]) => void;
    getOperatorsForField: (field: RuleField) => readonly RuleOperator[];
    accounts?: { id: string; name: string; institution_name?: string }[];
    transactionForPrefill?: Transaction | null;
    budgetCategories?: BudgetCategory[];
    budgetHeaders?: BudgetHeader[];
}

export const RuleConditionsControls: React.FC<RuleConditionsControlsProps> = ({
    conditions,
    onChange,
    getOperatorsForField,
    accounts = [],
    transactionForPrefill,
    budgetCategories,
    budgetHeaders,
}) => {
    // Add a new condition
    const handleAddCondition = () => {
        onChange([...conditions, createEmptyCondition()]);
    };

    // Remove a condition
    const handleRemoveCondition = (index: number) => {
        const updatedConditions = [...conditions];
        updatedConditions.splice(index, 1);
        onChange(updatedConditions);
    };

    // Update a condition's field
    const handleFieldChange = (index: number, field: RuleField) => {
        const updatedConditions = [...conditions];
        let defaultValue = '';

        if (transactionForPrefill) {
            switch (field) {
                case 'merchant_name':
                    defaultValue = transactionForPrefill.merchant_name || '';
                    break;
                case 'description':
                    defaultValue = transactionForPrefill.description || '';
                    break;
                case 'account':
                    defaultValue = String(transactionForPrefill.account_id || '');
                    break;
                case 'category':
                    defaultValue = String(transactionForPrefill.category_id || '');
                    break;
                case 'amount':
                    defaultValue = Math.abs(transactionForPrefill.amount || 0).toString();
                    break;
                case 'date':
                    defaultValue = transactionForPrefill.date ? (typeof transactionForPrefill.date === 'string' ? transactionForPrefill.date : new Date(transactionForPrefill.date).toISOString()) : new Date().toISOString();
                    break;
                case 'transaction_type':
                    defaultValue = transactionForPrefill.transaction_type || 'expense';
                    break;
                case 'recurring':
                    defaultValue = typeof transactionForPrefill.recurring === 'boolean' ? (transactionForPrefill.recurring ? 'true' : 'false') : 'false';
                    break;
                case 'location':
                    try {
                        const loc = typeof transactionForPrefill.location === 'string'
                            ? JSON.parse(transactionForPrefill.location)
                            : transactionForPrefill.location;
                        defaultValue = String(loc?.manual_input || loc?.city || '');
                    } catch (_) {
                        defaultValue = '';
                    }
                    break;
                default:
                    if (field === 'amount') defaultValue = '0';
                    else if (field === 'date') defaultValue = new Date().toISOString();
                    else if (field === 'recurring') defaultValue = 'false';
                    else if (field === 'transaction_type') defaultValue = 'expense';
                    else if (field === 'category') defaultValue = '';
                    else defaultValue = '';
                    break;
            }
        } else {
            if (field === 'amount') {
                defaultValue = '0';
            } else if (field === 'date') {
                defaultValue = new Date().toISOString();
            } else if (field === 'recurring') {
                defaultValue = 'false';
            } else if (field === 'transaction_type') {
                defaultValue = 'expense';
            } else if (field === 'category') {
                defaultValue = '';
            }
        }

        updatedConditions[index] = {
            ...updatedConditions[index],
            field: field,
            operator: getOperatorsForField(field)[0],
            value: defaultValue,
            negated: updatedConditions[index].negated || false,
        };
        onChange(updatedConditions);
    };

    // Update a condition's operator
    const handleOperatorChange = (index: number, operator: RuleOperator) => {
        const updatedConditions = [...conditions];
        const field = updatedConditions[index].field;
        let defaultValue = updatedConditions[index].value;

        if (operator === 'in_range' && field === 'date') {
            defaultValue = JSON.stringify({ start: new Date().toISOString(), end: new Date().toISOString() });
        } else if (operator === 'between' && field === 'amount') {
            defaultValue = '0,100';
        }

        updatedConditions[index] = {
            ...updatedConditions[index],
            operator: operator,
            value: defaultValue,
        };
        onChange(updatedConditions);
    };

    // Update a condition's value
    const handleValueChange = (index: number, value: string) => {
        const updatedConditions = [...conditions];
        updatedConditions[index] = {
            ...updatedConditions[index],
            value,
        };
        onChange(updatedConditions);
    };

    // Toggle negation for a condition
    const handleNegationChange = (index: number, negated: boolean) => {
        const updatedConditions = [...conditions];
        updatedConditions[index] = {
            ...updatedConditions[index],
            negated,
        };
        onChange(updatedConditions);
    };

    // Handle date range selection for 'in_range' operator
    const handleDateRangeChange = (index: number, type: 'start' | 'end', date: Date | null) => {
        const updatedConditions = [...conditions];
        const condition = updatedConditions[index];

        try {
            // Parse existing value as JSON
            const currentValue = condition.value ? JSON.parse(condition.value) : { start: null, end: null };

            // Update the appropriate date
            const updatedValue = {
                ...currentValue,
                [type]: date ? date.toISOString() : null,
            };

            // Store as JSON string
            updatedConditions[index] = {
                ...condition,
                value: JSON.stringify(updatedValue),
            };

            onChange(updatedConditions);
        } catch (e) {
            console.error("Error updating date range:", e);
        }
    };

    // Render the appropriate value input based on field and operator
    const renderValueInput = (condition: RuleCondition, index: number) => {
        const { field, operator, value } = condition;

        if (field === 'category') {
            const categoryOptions = budgetHeaders && budgetHeaders.length > 0 && budgetCategories && budgetCategories.length > 0
                ? budgetHeaders.map(header => ({
                    group: header.name,
                    items: header.categories
                        .map(cat => budgetCategories.find(bc => bc.id === cat.id))
                        .filter(Boolean)
                        .map(category => ({
                            value: String(category!.id),
                            label: category!.name,
                        }))
                }))
                : [];

            return (
                <Select
                    label="Category Name"
                    placeholder="Select category"
                    data={categoryOptions}
                    value={value}
                    onChange={(val) => handleValueChange(index, val || '')}
                    searchable
                    clearable
                />
            );
        }

        // For amount field with numeric input
        if (field === 'amount') {
            if (operator === 'between') {
                const [min, max] = value.split(',').map(v => v === '' ? 0 : parseFloat(v));
                return (
                    <Group grow>
                        <NumberInput
                            label="Min Amount"
                            placeholder="0.00"
                            decimalScale={2}
                            min={0}
                            value={min || 0}
                            onChange={(val) => {
                                const minVal = val || 0;
                                handleValueChange(index, `${minVal},${max || 0}`);
                            }}
                        />
                        <NumberInput
                            label="Max Amount"
                            placeholder="0.00"
                            decimalScale={2}
                            min={0}
                            value={max || 0}
                            onChange={(val) => {
                                const maxVal = val || 0;
                                handleValueChange(index, `${min || 0},${maxVal}`);
                            }}
                        />
                    </Group>
                );
            } else {
                return (
                    <NumberInput
                        label="Amount"
                        placeholder="Enter amount"
                        decimalScale={2}
                        min={0}
                        value={value === '' ? '' : parseFloat(value)}
                        onChange={(val) => handleValueChange(index, String(val))}
                    />
                );
            }
        }

        // For date field with date picker
        if (field === 'date') {
            if (operator === 'in_range') {
                try {
                    const dateRange = value ? JSON.parse(value) : { start: null, end: null };
                    return (
                        <Group grow>
                            <DatePickerInput
                                label="Start Date"
                                placeholder="Select start date"
                                value={dateRange.start ? new Date(dateRange.start) : null}
                                onChange={(date) => handleDateRangeChange(index, 'start', date)}
                            />
                            <DatePickerInput
                                label="End Date"
                                placeholder="Select end date"
                                value={dateRange.end ? new Date(dateRange.end) : null}
                                onChange={(date) => handleDateRangeChange(index, 'end', date)}
                                minDate={dateRange.start ? new Date(dateRange.start) : undefined}
                            />
                        </Group>
                    );
                } catch (e) {
                    console.error("Error parsing date range:", e);
                    return (
                        <TextInput
                            label="Date Range"
                            placeholder="Invalid date range format"
                            value={value}
                            onChange={(e) => handleValueChange(index, e.target.value)}
                        />
                    );
                }
            } else {
                return (
                    <DatePickerInput
                        label="Date"
                        placeholder="Select date"
                        value={value ? new Date(value) : null}
                        onChange={(date) => handleValueChange(index, date ? date.toISOString() : '')}
                    />
                );
            }
        }

        // For account field with dropdown of accounts
        if (field === 'account') {
            return (
                <Select
                    label="Account"
                    placeholder="Select an account"
                    data={accounts.map(account => ({
                        value: account.id.toString(),
                        label: `${account.name}${account.institution_name ? ` (${account.institution_name})` : ''}`,
                    }))}
                    value={value}
                    onChange={(val) => handleValueChange(index, val || '')}
                    searchable
                />
            );
        }

        // For transaction_type field with dropdown
        if (field === 'transaction_type') {
            return (
                <Select
                    label="Transaction Type"
                    placeholder="Select type"
                    data={[
                        { value: 'expense', label: 'Expense' },
                        { value: 'income', label: 'Income' },
                        { value: 'transfer', label: 'Transfer' },
                    ]}
                    value={value}
                    onChange={(val) => handleValueChange(index, val || '')}
                />
            );
        }

        // For recurring field with boolean dropdown
        if (field === 'recurring') {
            return (
                <Select
                    label="Recurring"
                    placeholder="Select"
                    data={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ]}
                    value={value}
                    onChange={(val) => handleValueChange(index, val || '')}
                />
            );
        }

        // Default case: text input for all other fields
        return (
            <TextInput
                label="Value"
                placeholder={`Enter ${field} value`}
                value={value}
                onChange={(e) => handleValueChange(index, e.target.value)}
            />
        );
    };

    return (
        <Stack gap="md">
            <Text fw={500} size="sm">Rule Conditions</Text>
            <Text size="xs" c="dimmed">
                All conditions must be met for the rule to apply (AND logic).
            </Text>

            <Box
                p="md"
                style={{
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    background: '#f9f9f9'
                }}
            >
                {conditions.map((condition, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && (
                            <Center my="md">
                                <Box style={{ position: 'relative', width: '100%' }}>
                                    <Divider
                                        my="xs"
                                        label={
                                            <ThemeIcon radius="xl" size="md" variant="light" color="blue">
                                                <img src={plusIcon} alt="AND" width={12} />
                                            </ThemeIcon>
                                        }
                                        labelPosition="center"
                                    />
                                </Box>
                            </Center>
                        )}

                        <Box
                            p="sm"
                            bg="white"
                            style={{
                                borderRadius: 4,
                                position: 'relative',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                        >
                            <Stack gap="xs">
                                <Group justify="space-between" align="center">
                                    <Text size="sm" fw={500}>Condition {index + 1}</Text>
                                    {conditions.length > 1 && (
                                        <ActionIcon
                                            color="red"
                                            variant="subtle"
                                            onClick={() => handleRemoveCondition(index)}
                                        >
                                            <img src={trashIcon} alt="Delete" width={16} />
                                        </ActionIcon>
                                    )}
                                </Group>
                                <Group grow>
                                    <Select
                                        label="Field"
                                        placeholder="Select field"
                                        data={[
                                            { value: 'merchant_name', label: 'Merchant Name' },
                                            { value: 'description', label: 'Description' },
                                            { value: 'category', label: 'Category' },
                                            { value: 'account', label: 'Account' },
                                            { value: 'location', label: 'Location' },
                                            { value: 'amount', label: 'Amount' },
                                            { value: 'date', label: 'Date' },
                                            { value: 'transaction_type', label: 'Transaction Type' },
                                            { value: 'recurring', label: 'Recurring' },
                                        ]}
                                        value={condition.field}
                                        onChange={(val) => val && handleFieldChange(index, val as RuleField)}
                                    />
                                    <Select
                                        label="Operator"
                                        placeholder="Select operator"
                                        data={getOperatorsForField(condition.field).map(op => {
                                            const labels: Record<string, string> = {
                                                'equals': 'Equals',
                                                'contains': 'Contains',
                                                'greater_than': 'Greater Than',
                                                'less_than': 'Less Than',
                                                'between': 'Between',
                                                'in_range': 'In Range',
                                            };
                                            return {
                                                value: op,
                                                label: labels[op] || op,
                                            };
                                        })}
                                        value={condition.operator}
                                        onChange={(val) => val && handleOperatorChange(index, val as RuleOperator)}
                                    />
                                </Group>
                                <Switch
                                    label="NOT (negate this condition)"
                                    checked={condition.negated || false}
                                    onChange={(event) => handleNegationChange(index, event.currentTarget.checked)}
                                    color="red"
                                />
                                {renderValueInput(condition, index)}
                            </Stack>
                        </Box>
                    </React.Fragment>
                ))}
            </Box>

            {conditions.length < 5 && (
                <Button
                    leftSection={<img src={plusIcon} alt="Add" width={16} />}
                    variant="outline"
                    color="blue"
                    onClick={handleAddCondition}
                >
                    Add Another Condition
                </Button>
            )}
        </Stack>
    );
}; 